import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Subject, BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { takeUntil, startWith, switchMap, map } from 'rxjs/operators';
import { DashboardService } from '../../core/services/dashboard.service';
import { StatusManageService, DashboardStats } from '../../core/services/status-manage.service';
import { AuthService } from '../../core/services/auth.service';
import { AccessControlService } from '../../core/services/access-control.service';
import { CompanyDashboardCardKey } from '../../core/models';
import { NotificationModalComponent } from '../positions/components/notification-modal/notification-modal.component';
import {
  Car,
  ChevronDown,
  ChevronsLeftRightEllipsis,
  ChevronUp,
  House,
  KeyRound,
  LucideAngularModule,
  MapPinHouse,
  Megaphone,
  MessageSquare,
  SquareFunction,
} from 'lucide-angular';
import { CheckinsListComponent } from '../checkins/components/checkins-list/checkins-list.component';
import { PatrolsListComponent } from '../patrols/components/patrols-list/patrols-list.component';
import { GuardsListComponent } from '../guards/components/guards-list/guards-list.component';
import { SupervisionComponent } from '../supervision/supervision.component';
import { DevicesListComponent } from '../devices/components/devices-list/devices-list.component';
import { UsersListComponent } from '../users/components/users-list/users-list.component';
import { PositionsListComponent } from '../positions/components/positions-list/positions-list.component';
import { WhatsAppConnectionsComponent } from '../whatsapp/components/whatsapp-list/whatsapp-connections.component';
import { AccessRequestsListComponent } from '../access-requests/components/access-requests-list/access-requests-list.component';
import { NotificationsListComponent } from '../notification/component/notifications-list/notifications-list.component';
import { TeltonikaTrackingComponent } from '../devices/components/teltonika-tracking/teltonika-tracking.component';
import { ReliefGuardsListComponent } from '../relief-guards/relief-guard-list.component';
import { SigosAbsencesListComponent } from '../sigos-absence/sigos-absence-list.component';
// import {DevicesListComponent} from "../devices/components/devices-list/devices-list.component"
import {
  LayoutDashboard,
  Users,
  Shield,
  MapPin,
  Clock,
  Smartphone,
  AlertTriangle,
  RefreshCw,
  UserCheck,
  UserPlus,
  Settings,
  Bell,
  CheckCircle,
  XCircle,
  Route,
  Menu,
  ChevronLeft,
  LogOut,
  Building2,
  Cable,
  BellRing,
  Blocks,
  Radar,
  Navigation,
  ArrowLeftRight,
  UserX,
} from 'lucide-angular';
import { ActivatedRoute } from '@angular/router';
import { AccessRecordListComponent } from '../access-records/components/access-records-list/access-records-list.component';
import { EstablishmentListComponent } from '../establishment/components/establishments-list/establishment-list.component';
import { EstablishmentBlockComponent } from '../establishment-blocks/components/establishment-block-list/establishment-block';
import {
  Action,
  RolePermission,
  RolePermissionService,
} from '../../core/services/role-permission.service';
import { PatrolAlertsComponent } from '../patrol-alerts/components/alerts-component/patrol-alerts.component';
import { AlertsPage } from '../patrol-alerts/components/alert-page/alerts.page';
import { PositionAccessListComponent } from '../position-access/position-access-list.component';
interface DashboardCard {
  key: CompanyDashboardCardKey;
  title: string;
  value: number;
  detail?: string;
  icon: any;
  route: string;
}

interface MenuItem {
  label: string;
  icon: any;
  route: string;
  badge?: number;
  key: string;
  mainSection?: string;
  hide: boolean;
  deviceGroup?: boolean;
  userGroup?: boolean;
  accessGroup?: boolean;
  alertGroup?: boolean;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    AlertsPage,
    PatrolAlertsComponent,
    AccessRecordListComponent,
    EstablishmentListComponent,
    EstablishmentBlockComponent,
    RouterModule,
    NotificationsListComponent,
    WhatsAppConnectionsComponent,
    LucideAngularModule,
    AccessRequestsListComponent,
    PositionsListComponent,
    UsersListComponent,
    DevicesListComponent,
    SupervisionComponent,
    GuardsListComponent,
    CheckinsListComponent,
    NotificationModalComponent,
    PatrolsListComponent,
    TeltonikaTrackingComponent,
    ReliefGuardsListComponent,
    SigosAbsencesListComponent,
    PositionAccessListComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dashboard.component.html',
  styles: [],
  host: { style: 'display:flex; flex-direction:column; height:100%; min-height:0; width:100%;' },
})
export class DashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private baseMenuItems: MenuItem[] = [
    {
      label: 'Dashboard',
      icon: LayoutDashboard,
      route: '/dashboard',
      key: 'dashboard',
      hide: false,
    },
    { label: 'Check-Presence', icon: Clock, route: '/checkins', key: 'checkins', hide: false },
    { label: 'Rondas', icon: Route, route: '/patrols', key: 'patrols', hide: false },
    {
      label: 'Rendições',
      icon: ArrowLeftRight,
      route: '/relief-guards',
      key: 'reliefGuards',
      hide: false,
    },
    { label: 'Ausências', icon: UserX, route: '/sigos-absences', key: 'sigos', hide: false },
    { label: 'Vigilantes', icon: Shield, route: '/guards', key: 'guards', hide: false },
    {
      label: 'Supervisores',
      icon: UserCheck,
      route: '/supervision',
      key: 'supervisors',
      hide: false,
    },
    { label: 'Rastreamento', icon: Navigation, route: '/teltonika', key: 'teltonika', hide: false },
    { label: 'Utilizadores', icon: Users, route: '/users', key: 'users', hide: false },
    { label: 'Postos', icon: MapPin, route: '/positions', key: 'positions', hide: false },
    { label: 'WhatsApp', icon: Cable, route: '/connections', key: 'connections', hide: false },
    {
      label: 'Notificações',
      icon: BellRing,
      route: '/notifications',
      key: 'notifications',
      hide: false,
    },
    {
      label: 'Dispositivos',
      icon: Smartphone,
      route: '/devices',
      key: 'devices',
      hide: false,
      deviceGroup: true,
    },
    {
      label: 'Requisições',
      icon: AlertTriangle,
      route: '/access-requests',
      key: 'requests',
      hide: false,
      deviceGroup: true,
    },
    {
      label: 'Pedidos de Acesso',
      icon: KeyRound,
      route: '/position-access',
      key: 'position_access',
      hide: false,
      deviceGroup: true,
    },
    {
      label: 'Residências (Casa)',
      icon: MapPinHouse,
      route: '/establishment',
      key: 'establishments',
      hide: false,
      accessGroup: true,
    },
    {
      label: 'Blocos',
      icon: Blocks,
      route: '/vehicles',
      key: 'vehicle_access',
      hide: false,
      accessGroup: true,
    },
    {
      label: 'Entradas/Saídas',
      icon: ChevronsLeftRightEllipsis,
      route: '/access_records',
      key: 'access_records',
      hide: false,
      accessGroup: true,
    },
    // { label:"Alertas", icon:ChevronsLeftRightEllipsis, route:"/patrol_alerts", key:"patrol_alerts", hide:false ,alertGroup:true },
    {
      label: 'Alertas',
      icon: Megaphone,
      route: '/alerts',
      key: 'alerts',
      hide: false,
      alertGroup: true,
    },
    // { label: 'Chat', icon: MessageSquare, route: '/chat', key: 'chat', hide: false },
  ];

  menuItems = computed<MenuItem[]>(() => {
    const permissions = this.userPermitions();

    return this.baseMenuItems.map((item) => {
      // Aplicamos o filtro dinâmico apenas nas chaves que importam
      if (item.key !== 'dashboard') {
        return {
          ...item,
          hide: this.userHasPermitionOfTheMenu(item.key),
        };
      }
      return item;
    });
  });

  constructor(
    private dashboardService: DashboardService,
    private statusManageService: StatusManageService,
    public authService: AuthService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private accessControlService: AccessControlService,
    private route: ActivatedRoute,
    private rolesAndPermitionService: RolePermissionService,
  ) {}

  sidebarOpen = false;
  // Observables públicos do StatusManageService
  stats$!: Observable<DashboardStats>;
  isSyncing$!: Observable<boolean>;
  appStatus$!: Observable<{
    isOnline: boolean;
    lastSync: Date | null;
    syncInProgress: boolean;
    error: string | null;
  }>;
  lastSync$!: Observable<Date | null>;

  // Observable para os cards do dashboard (sempre disponível)
  dashboardCards$!: Observable<DashboardCard[]>;

  // Observable para postos com checkins
  positionsWithCheckins$!: Observable<any[]>;
  canShowPositionsTable$!: Observable<boolean>;
  // Cards to show
  isCollapsed = signal<boolean>(false);
  isCollapsedUser = signal<boolean>(false);
  isDispositivosCollapsed = signal<boolean>(false);
  isCollapsedAccess = signal<boolean>(false);
  igonredRoles = [
    'dashboard',
    'checkins',
    'establishments',
    'teltonika',
    'access_records',
    'vehicle_access',
    'alerts',
    'reliefGuards',
    'sigos',
    'chat',
    'position_access',
  ];

  sideBarView = signal<Record<string, boolean>>({
    checkins: true,

    positions: true,
    guards: true,
    devices: true,
    supervisors: true,
    users: true,
    functions: true,
    requests: true,
    positionsCheckinsTable: true,
    patrols: true,
    reliefGuards: true,
    sigos: true,
    connections: false,
    establishment: false,
    vehicles: false,
    access_records: false,
    teltonika: true,
  });

  isMenuItemActive(key: string): boolean {
    if (this.igonredRoles.includes(key)) return true; // ignorar roles que não fazem parte do dashboard
    // return true;
    return this.sideBarView()[key] === true;
  }

  userPermitions = signal<RolePermission[]>([]);
  filteredMenuItems = computed(() => {
    return this.menuItems().filter(
      (item) =>
        this.isMenuItemActive(item.key) &&
        !item.hide &&
        !item.deviceGroup &&
        !item.userGroup &&
        !item.accessGroup,
    );
  });
  filteredMenuItemCollapsed = computed(() => {
    return this.menuItems().filter((item) => this.isMenuItemActive(item.key) && !item.hide);
  });

  filteredDispositivosItems = computed(() => {
    return this.menuItems().filter((item) => item.deviceGroup && this.isMenuItemActive(item.key));
  });
  filteredUserItems = computed(() => {
    return this.menuItems().filter((item) => item.userGroup && this.isMenuItemActive(item.key));
  });
  filteredAccessItems = computed(() => {
    return this.menuItems().filter((item) => item.accessGroup && this.isMenuItemActive(item.key));
  });

  hasAccessItems = computed(() => this.filteredAccessItems().length > 0);
  hasDispositivosItems = computed(() => this.filteredDispositivosItems().length > 0);
  hasUserItems = computed(() => this.filteredUserItems().length > 0);

  userHasPermitionOfTheMenu(keyItem: string): boolean {
    let key = keyItem;

    // keys que a DB salvou com um nome diferente da  menu imte
    if (keyItem == 'patrols') {
      key = 'rotations';
    }
    if (keyItem == 'checkins') {
      key = 'check_ins';
    }
    if (keyItem == 'teltonika') {
      key = 'teltonika_alerts';
    }
    if (keyItem == 'requests') {
      key = 'device_requests';
    }
    if (keyItem == 'alerts') {
      key = 'patrol_alerts';
    }
    if (keyItem == 'supervisors') {
      key = 'supervisor_check_ins';
    }
    if (keyItem == 'reliefGuards') {
      key = 'relief_guards';
    }
    if (keyItem == 'sigos') {
      key = 'sigos_absences';
    }
    // if (keyItem == 'chat') {
    //   key = 'chat_messages';
    // }
    if (!this.userPermitions()) {
      return false;
    }
    if (this.userPermitions().length == 0) {
      return false;
    }
    const actionsUsers: Action[] = this.userPermitions()
      .map((actionTemp) => actionTemp?.action)
      .filter((action): action is Action => action !== undefined);
    // const actionsUsers:Action[]  = this.userPermitions().map((actionTemp)=>actionTemp?.action);

    if (!actionsUsers) {
      return false;
    }
    const allDesignation: string[] = actionsUsers.map(
      (temp: Action) => temp.designation.split(':')[0],
    );
    if ((keyItem === 'reliefGuards' || keyItem === 'sigos') && !allDesignation.includes(key)) {
      return false;
    }
    return !allDesignation.includes(key);
  }

  // Modal de notificação
  showNotificationModal = false;
  selectedPositionForNotification: any = null;
  activeSection = this.menuItems()[0];

  private activeSectionSubject = new BehaviorSubject<MenuItem>(this.menuItems()[0]);
  activeSection$ = this.activeSectionSubject.asObservable();

  // Icons
  readonly LayoutDashboard = LayoutDashboard;
  readonly Users = Users;
  readonly Shield = Shield;
  readonly MapPin = MapPin;
  readonly Clock = Clock;
  readonly Smartphone = Smartphone;
  readonly AlertTriangle = AlertTriangle;
  readonly RefreshCw = RefreshCw;
  readonly UserCheck = UserCheck;
  readonly UserPlus = UserPlus;
  readonly Settings = Settings;
  readonly Bell = Bell;
  readonly CheckCircle = CheckCircle;
  readonly XCircle = XCircle;
  readonly Route = Route;
  readonly Menu = Menu;
  readonly ChevronLeft = ChevronLeft;
  readonly ChevronUp = ChevronUp;
  readonly ChevronDown = ChevronDown;
  readonly LogOut = LogOut;
  readonly Building2 = Building2;
  readonly MapPinHouse = MapPinHouse;
  readonly KeyRound = KeyRound;
  readonly ChevronsLeftRightEllipsis = ChevronsLeftRightEllipsis;
  readonly Megaphone = Megaphone;
  readonly Car = Car;
  readonly Blocks = Blocks;
  readonly House = House;
  readonly Radar = Radar;
  readonly Navigation = Navigation;
  readonly ArrowLeftRight = ArrowLeftRight;
  readonly UserX = UserX;
  readonly MessageSquare = MessageSquare;

  ngOnInit(): void {
    const token = this.authService.getToken();

    if (!token) {
      this.router.navigate(['/']);
      return;
    }

    this.loadPermitions();
    this.isSyncing$ = this.statusManageService.isSyncing$;
    this.appStatus$ = this.statusManageService.appStatus$;
    this.lastSync$ = this.statusManageService.lastSync$;

    this.stats$ = this.dashboardService.getDashboardStats().pipe(startWith(this.getEmptyStats()));
    this.dashboardCards$ = combineLatest([this.stats$, this.accessControlService.config$]).pipe(
      map(([stats]) =>
        this.getDashboardCards(stats).filter((card) =>
          this.accessControlService.isDashboardCardEnabled(card.key),
        ),
      ),
      startWith(this.getDashboardCards(this.getEmptyStats())),
    );

    this.loadPositionsWithCheckins();
    this.canShowPositionsTable$ = this.accessControlService.config$.pipe(
      map(() => this.accessControlService.isDashboardCardEnabled('positionsCheckinsTable')),
    );

    // Usar config já carregada pelo AccessControlService (evita chamada HTTP duplicada)
    this.accessControlService.config$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (config) => {
        if (config?.dashboard_cards) {
          this.sideBarView.set(config.dashboard_cards);
          this.cdr.markForCheck();
        }
      },
      error: (error) => console.error('Erro ao carregar configuração:', error),
    });

    // Atualizar badges do menu
    this.updateMenuBadges();

    // Verificar se há uma seção salva ou da URL
    this.checkInitialSection();
  }

  checkInitialSection(): void {
    const savedSection = localStorage.getItem('dashboardActiveSection');
    if (savedSection) {
      const section = this.menuItems().find((item) => item.key === savedSection);
      if (section) {
        this.activeSection = section;
        this.activeSectionSubject.next(section);
      }
    }
  }
  loadPermitions(): void {
    if (!this.authService) {
      console.error('AuthService não foi injetado!');
      return;
    }
    const user = this.authService.getCurrentUser();

    if (!user?.id) return;

    this.rolesAndPermitionService.getUserPermissions(Number(user?.id)).subscribe({
      next: (response) => {
        const data = response?.data || response || [];
        this.userPermitions.set(data);
      },
      error: (error) => {
        console.error('Erro ao carregar conexões:', error);
      },
    });
  }

  updateMenuBadges(): void {
    this.stats$.subscribe((stats) => {
      const requestsBadge = (stats?.pendingRequests || 0) + (stats?.pendingAccessRequests || 0);
      const menuItem = this.menuItems().find((item) => item.label === 'Solicitações');
      if (menuItem) {
        menuItem.badge = requestsBadge > 0 ? requestsBadge : undefined;
        this.cdr.markForCheck();
      }
    });
  }
  toggleCollapsed(): void {
    this.isCollapsed.set(!this.isCollapsed());
  }
  toggleCollapsedUser(): void {
    this.isCollapsedUser.set(!this.isCollapsedUser());
  }

  toggleDispositivosCollapsed(): void {
    this.isDispositivosCollapsed.set(!this.isDispositivosCollapsed());
  }

  loadPositionsWithCheckins(): void {
    this.positionsWithCheckins$ = this.statusManageService.positions$.pipe(
      switchMap((positions: any[]) => {
        return this.statusManageService.checkins$.pipe(
          map((checkins: any[]) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const todayCheckins = checkins.filter((checkin: any) => {
              const checkinDate = new Date(checkin.check_time);
              checkinDate.setHours(0, 0, 0, 0);
              return checkinDate.getTime() === today.getTime();
            });

            return positions.map((position: any) => {
              const positionCheckins = todayCheckins.filter(
                (checkin: any) => String(checkin.position_id) === String(position.id),
              );

              const validCheckinsCount = positionCheckins.filter(
                (checkin: any) => checkin.status === 'valid',
              ).length;

              const invalidCheckinsCount = positionCheckins.filter(
                (checkin: any) => checkin.status === 'invalid',
              ).length;

              return {
                ...position,
                todayCheckinsCount: positionCheckins.length,
                validCheckinsCount: validCheckinsCount,
                invalidCheckinsCount: invalidCheckinsCount,
              };
            });
          }),
        );
      }),
      startWith([]),
    );
  }

  toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
  }

  isActiveRoute(route: string): boolean {
    return this.activeSection.key === route;
  }

  getUserInitials(): string {
    const user = this.authService.getCurrentUser();
    if (!user?.first_name) return 'U';
    return user.first_name
      .split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/']);
  }

  forceSync(): void {
    this.refreshStatistics();
    this.statusManageService.forceSync().subscribe({
      next: () => this.cdr.markForCheck(),
      error: () => this.cdr.markForCheck(),
    });
  }

  refreshStatistics(): void {
    this.stats$ = this.dashboardService.getDashboardStats().pipe(startWith(this.getEmptyStats()));
    this.dashboardCards$ = combineLatest([this.stats$, this.accessControlService.config$]).pipe(
      map(([stats]) =>
        this.getDashboardCards(stats).filter((card) =>
          this.accessControlService.isDashboardCardEnabled(card.key),
        ),
      ),
      startWith(this.getDashboardCards(this.getEmptyStats())),
    );
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getDashboardCards(stats: DashboardStats): DashboardCard[] {
    return [
      // {
      //   key: 'checkins',
      //   title: 'Check-Presence',
      //   value: stats?.todayCheckIns || 0,
      //   detail: stats?.last7DaysCheckIns ? `${stats.last7DaysCheckIns} últimos 7 dias` : 'Hoje',
      //   icon: this.Clock,
      //   route: '/checkins',
      // },
      {
        key: 'patrols',
        title: 'Rotação',
        value: stats?.totalPatrolRoutes || 0,
        detail: this.getPatrolsDetail(stats),
        icon: this.Route,
        route: '/patrols',
      },
      {
        key: 'guards',
        title: 'Vigilantes',
        value: stats?.totalGuards || 0,
        detail: this.getGuardsDetail(stats),
        icon: this.Shield,
        route: '/guards',
      },
      {
        key: 'supervisors',
        title: 'Supervisores',
        value: stats?.totalSupervisors || 0,
        detail: this.getSupervisorsDetail(stats),
        icon: this.UserCheck,
        route: '/supervision',
      },
      {
        key: 'devices',
        title: 'Dispositivos',
        value: stats?.totalDevices || 0,
        detail: this.getDevicesDetail(stats),
        icon: this.Smartphone,
        route: '/devices',
      },
      {
        key: 'users',
        title: 'Utilizadores',
        value: stats?.totalUsers || 0,
        detail: stats?.totalUsers
          ? `${stats.totalUsers} usuário${stats.totalUsers !== 1 ? 's' : ''}`
          : undefined,
        icon: this.Users,
        route: '/users',
      },
      {
        key: 'positions',
        title: 'Postos de Vigilância',
        value: stats?.totalPositions || 0,
        detail: this.getPositionsDetail(stats),
        icon: this.MapPin,
        route: '/positions',
      },
      {
        key: 'requests',
        title: 'Solicitações',
        value: (stats?.pendingRequests || 0) + (stats?.pendingAccessRequests || 0),
        detail: stats?.pendingRequests
          ? `${stats.pendingRequests} pendente${stats.pendingRequests !== 1 ? 's' : ''}`
          : undefined,
        icon: this.AlertTriangle,
        route: '/access-requests',
      },
    ];
  }

  private getPatrolsDetail(stats: DashboardStats): string {
    const details: string[] = [];

    if (stats?.patrolsCompletedToday !== undefined && stats.patrolsCompletedToday > 0) {
      details.push(
        `${stats.patrolsCompletedToday} concluída${stats.patrolsCompletedToday !== 1 ? 's' : ''} hoje`,
      );
    }

    if (stats?.patrolsInProgress !== undefined && stats.patrolsInProgress > 0) {
      details.push(`${stats.patrolsInProgress} em andamento`);
    }

    if (stats?.activePatrolRoutes !== undefined && stats.activePatrolRoutes > 0) {
      details.push(
        `${stats.activePatrolRoutes} rota${stats.activePatrolRoutes !== 1 ? 's' : ''} ativa${stats.activePatrolRoutes !== 1 ? 's' : ''}`,
      );
    }

    return details.length > 0 ? details.join(' • ') : 'Nenhuma patrulha hoje';
  }

  private getGuardsDetail(stats: DashboardStats): string {
    const details: string[] = [];

    if (stats?.activeGuards !== undefined && stats.activeGuards > 0) {
      details.push(`${stats.activeGuards} activo${stats.activeGuards !== 1 ? 's' : ''}`);
    }

    if (stats?.inactiveGuards !== undefined && stats.inactiveGuards > 0) {
      details.push(`${stats.inactiveGuards} inactivo${stats.inactiveGuards !== 1 ? 's' : ''}`);
    }

    return details.length > 0 ? details.join(' / ') : 'Nenhum vigilante';
  }

  private getSupervisorsDetail(stats: DashboardStats): string {
    const details: string[] = [];

    if (stats?.totalSupervisors !== undefined && stats.totalSupervisors > 0) {
      details.push(
        `${stats.totalSupervisors} supervisor${stats.totalSupervisors !== 1 ? 'es' : ''}`,
      );
    }

    if (stats?.activeSchedules !== undefined && stats.activeSchedules > 0) {
      details.push(
        `${stats.activeSchedules} agenda${stats.activeSchedules !== 1 ? 's' : ''} ativa${stats.activeSchedules !== 1 ? 's' : ''}`,
      );
    }

    return details.length > 0 ? details.join(' • ') : 'Nenhum supervisor';
  }

  private getDevicesDetail(stats: DashboardStats): string {
    const details: string[] = [];

    if (stats?.activeDevices !== undefined && stats.activeDevices > 0) {
      details.push(`${stats.activeDevices} activo${stats.activeDevices !== 1 ? 's' : ''}`);
    }

    if (stats?.pendingRequests !== undefined && stats.pendingRequests > 0) {
      details.push(`${stats.pendingRequests} pendente${stats.pendingRequests !== 1 ? 's' : ''}`);
    }

    return details.length > 0 ? details.join(' • ') : 'Nenhum dispositivo';
  }

  private getPositionsDetail(stats: DashboardStats): string {
    const details: string[] = [];

    if (stats?.activePositions !== undefined && stats.activePositions > 0) {
      details.push(`${stats.activePositions} activo${stats.activePositions !== 1 ? 's' : ''}`);
    }

    if (stats?.inactivePositions !== undefined && stats.inactivePositions > 0) {
      details.push(
        `${stats.inactivePositions} inactivo${stats.inactivePositions !== 1 ? 's' : ''}`,
      );
    }

    if (stats?.positionsWithoutCheckIn24h !== undefined && stats.positionsWithoutCheckIn24h > 0) {
      details.push(`${stats.positionsWithoutCheckIn24h} sem check-in 24h`);
    }

    return details.length > 0 ? details.join(' • ') : 'Nenhum posto';
  }

  private getEmptyStats(): DashboardStats {
    return {
      totalGuards: 0,
      activeGuards: 0,
      inactiveGuards: 0,
      totalPositions: 0,
      activePositions: 0,
      inactivePositions: 0,
      totalUsers: 0,
      activeUsers: 0,
      totalDevices: 0,
      activeDevices: 0,
      todayCheckIns: 0,
      last7DaysCheckIns: 0,
      pendingRequests: 0,
      totalAccessRequests: 0,
      pendingAccessRequests: 0,
      totalSupervisors: 0,
      activeSchedules: 0,
      totalPatrolRoutes: 0,
      activePatrolRoutes: 0,
      totalCheckpoints: 0,
      patrolsCompletedToday: 0,
      patrolsInProgress: 0,
      patrolsToday: 0,
      complianceRate: 0,
      positionsWithoutCheckIn24h: 0,
    };
  }

  trackByCardTitle(index: number, card: any): string {
    return card.key || card.title;
  }

  navigateToPage(route: string): void {
    const currentUser = this.authService.getCurrentUser();
    const companyId = currentUser?.company_id;

    const selectedCheckin = this.menuItems().find((menu) => menu.key === route);
    this.activeSection = selectedCheckin || this.menuItems()[0];

    if (selectedCheckin) {
      this.activeSection = selectedCheckin;
      this.activeSectionSubject.next(selectedCheckin);

      // Salvar para persistência
      localStorage.setItem('dashboardActiveSection', selectedCheckin.key);
    }

    // if (companyId) {
    //   this.router.navigate([route], {
    //     queryParams: { company_id: companyId },
    //   });
    // } else {
    //   this.router.navigate([route]);
    // }
    if (companyId) {
      // Pega os query params atuais
      const currentParams = this.route.snapshot.queryParams;

      const queryParams = { ...currentParams, company_id: companyId };

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: queryParams,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    // Fechar sidebar em dispositivos móveis após navegação
    if (window.innerWidth < 520) {
      this.sidebarOpen = false;
    }
  }
  // navigateToChat(): void {
  //   const currentUser = this.authService.getCurrentUser();
  //   const companyId = currentUser?.company_id;

  //   if (!companyId) {
  //     this.router.navigate(['/chat']);
  //     return;
  //   }

  //   const currentParams = this.route.snapshot.queryParams;
  //   const queryParams = { ...currentParams, company_id: companyId };

  //   this.router.navigate(['/chat'], { queryParams });
  // }

  navigateToChat(): void {
    const currentUser = this.authService.getCurrentUser();
    const companyId = currentUser?.company_id;

    const baseUrl = window.location.origin;

    const url = companyId ? `${baseUrl}/chat?company_id=${companyId}` : `${baseUrl}/chat`;

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  sendPushNotification(positionId: string, positionName: string): void {
    this.selectedPositionForNotification = {
      id: positionId,
      name: positionName,
    };
    this.showNotificationModal = true;
  }

  closeNotificationModal(): void {
    this.showNotificationModal = false;
    this.selectedPositionForNotification = null;
  }

  onNotificationSent(event: { devices: string[]; message: string }): void {
    console.log('Notificação enviada com sucesso:', event);
    this.cdr.markForCheck();
  }

  trackByPositionId(index: number, position: any): any {
    return position.id || index;
  }
}
